import { FC, useCallback, useEffect, useState } from 'react';

import {
  Avatar,
  Icon,
  IconButton,
  Skeleton,
  Typography,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { EmployeeFullInfo, getEmployeeInfo } from 'src/api/bookings';
import { messagesActions } from 'src/store/messages/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { defaultServerError } from 'src/utils/consts';

import { Link } from '../Link/Link';

import classes from './EmployeeInfo.module.scss';

interface InfoAttributeProps {
  iconName: Icons;
  loading: boolean;
  attribute?: string;
  to?: string;
}

const InfoAttribute: FC<InfoAttributeProps> = ({
  iconName,
  attribute,
  to,
  loading,
}) => {
  const dispatch = useAppDispatch();
  const copy = useCallback(() => {
    if (attribute) {
      navigator.clipboard.writeText(attribute);
      dispatch(
        messagesActions.show({
          message: `Скопировано в буфер обмена: ${attribute}`,
          delay: 2000,
        }),
      );
    }
  }, [attribute, dispatch]);

  return (
    <div className={classes.attributeContainer}>
      <Icon iconName={iconName} />
      {loading ? (
        <Skeleton className={classes.attributeSkeleton} />
      ) : (
        <Link to={to} navigationType='default'>
          {attribute}
        </Link>
      )}
      <div className={classes.copyButtonContainer}>
        <IconButton iconName={Icons.Copy} onClick={copy} disabled={loading} />
      </div>
    </div>
  );
};

export interface EmployeeInfoProps {
  employeeNumber: string;
  testId?: string;
}

export const EmployeeInfo: FC<EmployeeInfoProps> = ({
  employeeNumber,
  testId,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);

  const [employee, setEmployee] = useState<EmployeeFullInfo | undefined>();

  const getEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getEmployeeInfo(employeeNumber);
      setEmployee(
        response.employeeNumber === employeeNumber ? response : undefined,
      );
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, employeeNumber]);

  useEffect(() => {
    getEmployee();
  }, [getEmployee]);

  const name = `${employee?.lastName ?? 'Фамилия'} ${employee?.firstName ?? 'Имя'} ${employee?.middleName ?? ''}`;
  const position = employee?.positionName ?? 'Позиция';

  return (
    <div className={classes.container} data-testid={testId}>
      {loading ? (
        <Skeleton
          className={classes.avatar}
          data-testid={`${testId}PhotoSkeleton`}
        />
      ) : (
        <Avatar className={classes.avatar} src={employee?.photo} />
      )}
      <div className={classes.info}>
        {loading ? (
          <Skeleton
            className={classes.name}
            data-testid={`${testId}NameSkeleton`}
          />
        ) : (
          <Typography variant='body2' data-testid={`${testId}Name`}>
            {name}
          </Typography>
        )}
        {loading ? (
          <Skeleton
            className={classes.position}
            data-testid={`${testId}PositionSkeleton`}
          />
        ) : (
          <Typography variant='caption' data-testid={`${testId}Position`}>
            {position}
          </Typography>
        )}
        {employee?.phone && (
          <InfoAttribute
            iconName={Icons.Phone}
            attribute={employee.phone}
            to={`tel:${employee.phone}`}
            loading={loading}
          />
        )}
        {employee?.eMail && (
          <InfoAttribute
            iconName={Icons.Mail}
            attribute={employee.eMail}
            to={`mailto:${employee.eMail}`}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};
